import { onMounted, onUnmounted, ref } from 'vue'
import { THEME_BREAKPOINTS } from '~/utils/theme'

/**
 * @description 获取当前视口宽度，供组件外的命令式逻辑复用
 * @returns {number} 当前视口宽度
 */
export const getCurrentViewportWidth = () => {
  if (!import.meta.client) return parseInt(THEME_BREAKPOINTS.lg, 10)

  const widths = [
    window.innerWidth,
    document.documentElement?.clientWidth,
    window.visualViewport?.width,
  ]
    .map((width) => Math.round(Number(width) || 0))
    .filter((width) => width > 0)

  return widths.length ? Math.max(...widths) : parseInt(THEME_BREAKPOINTS.lg, 10)
}

/**
 * @description 当前视口是否小于指定断点，供组件外的命令式逻辑复用
 * @param {keyof typeof THEME_BREAKPOINTS} breakpoint - 断点名称
 * @returns {boolean} 是否小于断点
 */
export const isCurrentSmallerThanBreakpoint = (breakpoint) => {
  if (!import.meta.client) return false

  const breakpointWidth = parseInt(THEME_BREAKPOINTS[breakpoint], 10)
  const maxWidth = breakpointWidth - 1
  return (
    window.matchMedia(`(max-width: ${maxWidth}px)`).matches ||
    getCurrentViewportWidth() < breakpointWidth
  )
}

/**
 * @description 当前视口是否小于桌面断点 lg
 * @returns {boolean} 是否处于移动/平板紧凑布局
 */
export const isCurrentBelowDesktop = () => isCurrentSmallerThanBreakpoint('lg')

/**
 * @description 响应式媒体查询组合函数
 * @returns {Object} 媒体查询辅助函数
 */
export const useMediaQuery = () => {
  /**
   * @description 绑定媒体查询变化监听（兼容旧版 Safari/iOS）
   * @param {MediaQueryList} mediaQuery - window.matchMedia 返回对象
   * @param {(e: MediaQueryListEvent|MediaQueryList) => void} handler - 变化回调
   * @returns {() => void} 解绑函数
   * @reason iOS 旧版本仅支持 addListener/removeListener，不支持 addEventListener('change')
   */
  const bindMediaQueryChange = (mediaQuery, handler) => {
    if (!mediaQuery) return () => {}

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler)
      return () => {
        mediaQuery.removeEventListener('change', handler)
      }
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handler)
      return () => {
        mediaQuery.removeListener(handler)
      }
    }

    return () => {}
  }

  /**
   * @description 挂载后再注册媒体查询监听，避免 SSR 首帧与客户端视口不一致
   * @param {string} query - 媒体查询字符串
   * @param {import('vue').Ref<boolean>} matches - 匹配结果
   * @returns {void}
   */
  const registerMatchMedia = (query, matches) => {
    let unbind = () => {}

    onMounted(() => {
      if (!import.meta.client) return

      const mediaQuery = window.matchMedia(query)
      matches.value = mediaQuery.matches

      const updateMatches = (e) => {
        matches.value = Boolean(e && e.matches)
      }

      unbind = bindMediaQueryChange(mediaQuery, updateMatches)
    })

    onUnmounted(() => {
      unbind()
    })
  }

  const breakpoints = THEME_BREAKPOINTS

  const getExclusiveMaxWidth = (breakpoint) => {
    return `${parseInt(breakpoints[breakpoint], 10) - 1}px`
  }

  const createMediaQuery = (min, max = null) => {
    if (max) {
      return `(min-width: ${min}) and (max-width: ${getExclusiveMaxWidth(max)})`
    }
    return `(min-width: ${min})`
  }

  const getViewportWidth = getCurrentViewportWidth

  const isCurrentSmallerThan = (breakpoint) => {
    return isCurrentSmallerThanBreakpoint(breakpoint)
  }

  const isCurrentBelowDesktopFromComposable = () => isCurrentSmallerThan('lg')

  /**
   * @description 检查屏幕是否匹配指定断点
   * @param {string} breakpoint - 断点名称
   * @returns {boolean} 匹配结果
   */
  const useBreakpoint = (breakpoint) => {
    const matches = ref(false)
    registerMatchMedia(createMediaQuery(breakpoints[breakpoint]), matches)

    return matches
  }

  /**
   * @description 检查屏幕是否小于指定断点
   * @param {string} breakpoint - 断点名称
   * @returns {boolean} 匹配结果
   */
  const useSmallerThan = (breakpoint) => {
    const matches = ref(false)
    registerMatchMedia(`(max-width: ${getExclusiveMaxWidth(breakpoint)})`, matches)

    return matches
  }

  /**
   * @description 检查屏幕是否在两个断点之间
   * @param {string} min - 最小断点
   * @param {string} max - 最大断点
   * @returns {boolean} 匹配结果
   */
  const useBetween = (min, max) => {
    const matches = ref(false)
    registerMatchMedia(createMediaQuery(breakpoints[min], breakpoints[max]), matches)

    return matches
  }

  /**
   * @description 检查设备是否为手机
   * @returns {Ref<boolean>} 是否为手机
   */
  const isPhone = ref(false)

  /**
   * @description 检查设备是否为平板
   * @returns {Ref<boolean>} 是否为平板
   */
  const isTablet = ref(false)

  /**
   * @description 检查设备是否为桌面
   * @returns {Ref<boolean>} 是否为桌面
   */
  const isDesktop = ref(true)

  /**
   * @description 检查设备是否处于桌面以下的紧凑布局区间
   * @returns {Ref<boolean>} 是否小于桌面断点
   */
  const isBelowDesktop = ref(false)

  /**
   * @description 兼容旧代码的移动端别名（<= 1024 视为移动/紧凑布局）
   * @returns {Ref<boolean>} 是否处于桌面以下
   */
  const isMobile = isBelowDesktop

  /**
   * @description 检查设备是否处于紧凑桌面区间
   * @returns {Ref<boolean>} 是否为紧凑桌面
   */
  const isCompactDesktop = ref(false)

  /**
   * @description 检查设备是否处于标准 PC 区间
   * @returns {Ref<boolean>} 是否为标准 PC
   */
  const isWideDesktop = ref(false)

  /**
   * @description 获取当前断点
   * @returns {Ref<string>} 当前断点名称
   */
  const currentBreakpoint = ref('lg')

  const updateDeviceType = () => {
    if (import.meta.client) {
      const width = getViewportWidth()

      if (width < parseInt(breakpoints.sm)) {
        currentBreakpoint.value = 'xs'
      } else if (width < parseInt(breakpoints.md)) {
        currentBreakpoint.value = 'sm'
      } else if (width < parseInt(breakpoints.lg)) {
        currentBreakpoint.value = 'md'
      } else if (width < parseInt(breakpoints.xl)) {
        currentBreakpoint.value = 'lg'
      } else if (width < parseInt(breakpoints.xxl)) {
        currentBreakpoint.value = 'xl'
      } else if (width < parseInt(breakpoints.xxxl)) {
        currentBreakpoint.value = 'xxl'
      } else {
        currentBreakpoint.value = 'xxxl'
      }

      const mobileQuery = window.matchMedia(`(max-width: ${parseInt(breakpoints.md, 10) - 1}px)`)
      const tabletQuery = window.matchMedia(
        `(min-width: ${breakpoints.md}) and (max-width: ${getExclusiveMaxWidth('lg')})`
      )
      const desktopQuery = window.matchMedia(`(min-width: ${breakpoints.lg})`)
      const compactDesktopQuery = window.matchMedia(
        `(min-width: ${breakpoints.lg}) and (max-width: ${getExclusiveMaxWidth('xl')})`
      )
      const wideDesktopQuery = window.matchMedia(`(min-width: ${breakpoints.xl})`)

      isPhone.value = mobileQuery.matches
      isTablet.value = tabletQuery.matches
      isDesktop.value = desktopQuery.matches
      isBelowDesktop.value = isCurrentBelowDesktopFromComposable()
      isCompactDesktop.value = compactDesktopQuery.matches
      isWideDesktop.value = wideDesktopQuery.matches
    }
  }

  // 立即执行初始化
  if (import.meta.client) {
    let deviceTypeMediaQueryUnbinders = []
    let viewportResizeObserver = null

    onMounted(() => {
      updateDeviceType()
      window.addEventListener('resize', updateDeviceType)
      window.addEventListener('orientationchange', updateDeviceType)
      window.visualViewport?.addEventListener('resize', updateDeviceType)

      if (typeof ResizeObserver === 'function') {
        viewportResizeObserver = new ResizeObserver(updateDeviceType)
        viewportResizeObserver.observe(document.documentElement)
      }

      const deviceTypeQueries = [
        `(max-width: ${parseInt(breakpoints.md, 10) - 1}px)`,
        `(min-width: ${breakpoints.md}) and (max-width: ${getExclusiveMaxWidth('lg')})`,
        `(min-width: ${breakpoints.lg})`,
        `(min-width: ${breakpoints.lg}) and (max-width: ${getExclusiveMaxWidth('xl')})`,
        `(min-width: ${breakpoints.xl})`,
      ]

      deviceTypeMediaQueryUnbinders = deviceTypeQueries.map((query) =>
        bindMediaQueryChange(window.matchMedia(query), updateDeviceType)
      )
    })

    onUnmounted(() => {
      window.removeEventListener('resize', updateDeviceType)
      window.removeEventListener('orientationchange', updateDeviceType)
      window.visualViewport?.removeEventListener('resize', updateDeviceType)
      viewportResizeObserver?.disconnect()
      viewportResizeObserver = null
      deviceTypeMediaQueryUnbinders.forEach((unbind) => unbind())
      deviceTypeMediaQueryUnbinders = []
    })
  }

  /**
   * @description 自定义媒体查询
   * @param {string} query - 媒体查询字符串
   * @returns {Ref<boolean>} 匹配结果
   */
  const useCustomMediaQuery = (query) => {
    const matches = ref(false)
    registerMatchMedia(query, matches)

    return matches
  }

  return {
    breakpoints,
    useBreakpoint,
    useSmallerThan,
    useBetween,
    isPhone,
    isMobile,
    isTablet,
    isDesktop,
    isBelowDesktop,
    isCompactDesktop,
    isWideDesktop,
    currentBreakpoint,
    useCustomMediaQuery,
    updateDeviceType,
    getViewportWidth,
    isCurrentSmallerThan,
    isCurrentBelowDesktop: isCurrentBelowDesktopFromComposable,
  }
}
